/**
 * Each storefront's controlled vocabularies, and the mapping from the item's
 * own words into them: department, category, colour palette, condition, size
 * and material. Anything that doesn't map is left out and reported as a
 * warning rather than guessed into a value the storefront would reject.
 *
 * Carried over from the ThriftyBusiness Depop and Vinted exporters, which were
 * built against the storefronts' own forms and bulk templates (2026-08/09).
 */

export interface VocabItem {
  title: string | null;
  category: string | null;
  subcategory: string | null;
  audience: string | null;
  colorway: string | null;
  materials: string[];
  styleKeywords: string[];
}

export type Department = "Women" | "Men" | "Kids";

const WOMENS = /\bwomen'?s?\b|\bladies\b|\bgirls?\b|dress|skirt|blouse|legging|bra\b|heel|purse|handbag|romper|cami/i;
const MENS = /\bmen'?s?\b|\bguys\b|\bboys?\b/i;

function haystack(item: VocabItem): string {
  return [item.subcategory, item.category, item.title, item.styleKeywords.join(" ")].filter(Boolean).join(" ");
}

const titleCase = (s: string) => s.replace(/\w\S*/g, (w) => w[0]!.toUpperCase() + w.slice(1).toLowerCase());

/** Kids wins outright; adults are inferred from the words, defaulting to Women (flagged). */
export function department(item: VocabItem): { value: Department; guessed: boolean } {
  if (item.audience === "kids" || item.audience === "baby") return { value: "Kids", guessed: false };
  const text = haystack(item);
  if (MENS.test(text) && !WOMENS.test(text)) return { value: "Men", guessed: false };
  if (WOMENS.test(text)) return { value: "Women", guessed: false };
  return { value: "Women", guessed: true };
}

type PerDept = { women: string; men: string; kids: string };
const key = (d: Department) => d.toLowerCase() as keyof PerDept;

/** First match against the subcategory, then the category, then the title. */
function firstMatch<T>(item: VocabItem, table: Array<[RegExp, T]>): T | null {
  for (const source of [item.subcategory, item.category, item.title]) {
    if (!source) continue;
    const hit = table.find(([re]) => re.test(source));
    if (hit) return hit[1];
  }
  return null;
}

/* --- Depop ----------------------------------------------------------------------- */

export const DEPOP_CONDITION: Record<string, string> = {
  nwt: "Brand new",
  nwot: "Brand new",
  excellent: "Like new",
  great: "Used - Excellent",
  good: "Used - Good",
  fair: "Used - Fair",
};

/** The code Depop's create call takes for each condition label above. */
export const DEPOP_CONDITION_CODE: Record<string, string> = {
  "Brand new": "brand_new",
  "Like new": "used_like_new",
  "Used - Excellent": "used_excellent",
  "Used - Good": "used_good",
  "Used - Fair": "used_fair",
};

const DEPOP_COLOR: Record<string, string> = {
  black: "Black", white: "White", grey: "Grey", gray: "Grey",
  charcoal: "Grey", cream: "Cream", ivory: "Cream", offwhite: "Cream",
  beige: "Beige", tan: "Tan", camel: "Tan", brown: "Brown",
  chocolate: "Brown", orange: "Orange", rust: "Orange", red: "Red",
  burgundy: "Burgundy", maroon: "Burgundy", wine: "Burgundy",
  pink: "Pink", blush: "Pink", rose: "Pink", purple: "Purple",
  lavender: "Purple", lilac: "Purple", blue: "Blue", teal: "Blue",
  turquoise: "Blue", aqua: "Blue", navy: "Navy", green: "Green",
  olive: "Khaki", sage: "Green", mint: "Green", khaki: "Khaki",
  yellow: "Yellow", mustard: "Yellow", gold: "Gold", silver: "Silver",
  multi: "Multi", multicolor: "Multi", rainbow: "Multi",
};

const DEPOP_CATEGORY: Array<[RegExp, PerDept]> = [
  [/wind ?breaker|rain ?coat|puffer|parka|coat/i, { women: "Coats", men: "Coats", kids: "Coats" }],
  [/jacket|blazer|bomber/i, { women: "Jackets", men: "Jackets", kids: "Jackets" }],
  [/hoodie/i, { women: "Hoodies", men: "Hoodies", kids: "Hoodies" }],
  [/sweatshirt|crewneck|pullover/i, { women: "Sweatshirts", men: "Sweatshirts", kids: "Sweatshirts" }],
  [/sweater|jumper|cardigan|knit/i, { women: "Sweaters", men: "Sweaters", kids: "Sweaters" }],
  [/t-?shirt|\btee\b|graphic tee/i, { women: "T-shirts", men: "T-shirts", kids: "T-shirts" }],
  [/tank|cami(sole)?/i, { women: "Tops", men: "T-shirts", kids: "Tops" }],
  [/blouse|button[- ]?(up|down)|flannel|shirt/i, { women: "Blouses", men: "Shirts", kids: "Tops" }],
  [/\btop\b/i, { women: "Tops", men: "T-shirts", kids: "Tops" }],
  [/dress/i, { women: "Dresses", men: "Shirts", kids: "Dresses" }],
  [/romper|jumpsuit|overalls|one[- ]?piece/i, { women: "Jumpsuits", men: "Pants", kids: "Outfits & sets" }],
  [/skirt/i, { women: "Skirts", men: "Pants", kids: "Skirts" }],
  [/jeans|denim/i, { women: "Jeans", men: "Jeans", kids: "Jeans" }],
  [/legging|yoga pant/i, { women: "Leggings", men: "Pants", kids: "Pants" }],
  [/joggers?|sweatpant|track ?pant/i, { women: "Pants", men: "Pants", kids: "Pants" }],
  [/shorts/i, { women: "Shorts", men: "Shorts", kids: "Shorts" }],
  [/pants?|trousers|chinos|slacks/i, { women: "Pants", men: "Pants", kids: "Pants" }],
  [/swim|bikini|trunks/i, { women: "Swimwear", men: "Swimwear", kids: "Swimwear" }],
  [/sneaker|trainer|running shoe/i, { women: "Sneakers", men: "Sneakers", kids: "Shoes" }],
  [/boot/i, { women: "Boots", men: "Boots", kids: "Shoes" }],
  [/heel|pump|sandal|flat|loafer|shoe/i, { women: "Shoes", men: "Shoes", kids: "Shoes" }],
  [/purse|handbag|tote|crossbody|bag/i, { women: "Bags", men: "Bags", kids: "Accessories" }],
  [/hat|cap|beanie|scarf|belt|glove|accessor/i, { women: "Accessories", men: "Accessories", kids: "Accessories" }],
];

/** [department, Depop leaf], e.g. ["Women", "Leggings"]. */
export function depopCategory(item: VocabItem, dept: Department): string[] {
  const hit = firstMatch(item, DEPOP_CATEGORY);
  const leaf = hit ? hit[key(dept)] : item.category ? titleCase(item.category) : "Other";
  return [dept, leaf];
}

/** Up to two colours from Depop's palette. */
export function depopColors(colorway: string | null): string[] {
  if (!colorway) return [];
  const seen = new Set<string>();
  for (const token of colorway.toLowerCase().split(/[^a-z]+/)) {
    const mapped = DEPOP_COLOR[token];
    if (mapped) seen.add(mapped);
    if (seen.size === 2) break;
  }
  return [...seen];
}

const AGE: Array<[RegExp, string]> = [
  [/\by2k\b/i, "Y2K"],
  [/\b(19)?90s\b/i, "90s"],
  [/\b(19)?80s\b/i, "80s"],
  [/\b(19)?70s\b/i, "70s"],
  [/\b(19)?60s\b/i, "60s"],
];

export function depopAge(item: VocabItem): string {
  const text = haystack(item);
  for (const [re, age] of AGE) if (re.test(text)) return age;
  return "Modern";
}

export function depopSource(item: VocabItem): string {
  return /vintage/i.test(haystack(item)) ? "Vintage" : "Preloved";
}

/** Up to three style tags from the keywords; decades live in `age`, not here. */
export function depopStyles(item: VocabItem): string[] {
  const decades = /^(y2k|\d0s|vintage|retro)$/i;
  return item.styleKeywords.filter((k) => k && !decades.test(k.trim())).slice(0, 3).map((k) => titleCase(k.trim()));
}

/* --- Poshmark -------------------------------------------------------------------- */

export const POSHMARK_CONDITION: Record<string, string> = {
  nwt: "New With Tags",
  nwot: "New Without Tags",
  excellent: "Like New",
  great: "Good",
  good: "Good",
  fair: "Fair",
};

/** Poshmark's condition label → the code its listing API takes (the extension sends it; the item page previews it). */
export const POSHMARK_CONDITION_CODE: Record<string, string> = {
  "New With Tags": "nwt",
  "New Without Tags": "uln", // Poshmark has no NWOT grade; "Like New" is the closest
  "Like New": "uln",
  Good: "ug",
  Fair: "uf",
};

/** Poshmark's swatches, from Depop's palette (which the colorway maps into first). */
const POSHMARK_COLOR: Record<string, string> = {
  Black: "Black", White: "White", Grey: "Gray", Cream: "Cream", Beige: "Tan",
  Tan: "Tan", Brown: "Brown", Orange: "Orange", Red: "Red", Burgundy: "Red",
  Pink: "Pink", Purple: "Purple", Blue: "Blue", Navy: "Blue", Green: "Green",
  Khaki: "Green", Yellow: "Yellow", Gold: "Gold", Silver: "Silver",
};

/** Depop leaf → Poshmark [category, sub-category?] per department. */
const POSHMARK_CATEGORY: Record<string, { women: string[]; men: string[]; kids: string[] }> = {
  Coats:          { women: ["Jackets & Coats"], men: ["Jackets & Coats"], kids: ["Jackets & Coats"] },
  Jackets:        { women: ["Jackets & Coats"], men: ["Jackets & Coats"], kids: ["Jackets & Coats"] },
  Hoodies:        { women: ["Tops", "Sweatshirts & Hoodies"], men: ["Shirts", "Sweatshirts & Hoodies"], kids: ["Shirts & Tops"] },
  Sweatshirts:    { women: ["Tops", "Sweatshirts & Hoodies"], men: ["Shirts", "Sweatshirts & Hoodies"], kids: ["Shirts & Tops"] },
  Sweaters:       { women: ["Sweaters"], men: ["Sweaters"], kids: ["Shirts & Tops"] },
  "T-shirts":     { women: ["Tops", "Tees - Short Sleeve"], men: ["Shirts", "Tees - Short Sleeve"], kids: ["Shirts & Tops"] },
  Tops:           { women: ["Tops"], men: ["Shirts"], kids: ["Shirts & Tops"] },
  Blouses:        { women: ["Tops", "Blouses"], men: ["Shirts"], kids: ["Shirts & Tops"] },
  Shirts:         { women: ["Tops", "Button Down Shirts"], men: ["Shirts", "Casual Button Down Shirts"], kids: ["Shirts & Tops"] },
  Dresses:        { women: ["Dresses"], men: ["Shirts"], kids: ["Dresses"] },
  Jumpsuits:      { women: ["Pants & Jumpsuits"], men: ["Pants"], kids: ["One Pieces"] },
  "Outfits & sets": { women: ["Pants & Jumpsuits"], men: ["Pants"], kids: ["Matching Sets"] },
  Skirts:         { women: ["Skirts"], men: ["Pants"], kids: ["Bottoms"] },
  Jeans:          { women: ["Jeans"], men: ["Jeans"], kids: ["Bottoms"] },
  Leggings:       { women: ["Pants & Jumpsuits", "Leggings"], men: ["Pants"], kids: ["Bottoms"] },
  Pants:          { women: ["Pants & Jumpsuits"], men: ["Pants"], kids: ["Bottoms"] },
  Shorts:         { women: ["Shorts"], men: ["Shorts"], kids: ["Bottoms"] },
  Swimwear:       { women: ["Swim"], men: ["Swim"], kids: ["Swim"] },
  Sneakers:       { women: ["Shoes", "Sneakers"], men: ["Shoes", "Sneakers"], kids: ["Shoes"] },
  Boots:          { women: ["Shoes"], men: ["Shoes", "Boots"], kids: ["Shoes"] },
  Shoes:          { women: ["Shoes"], men: ["Shoes"], kids: ["Shoes"] },
  Bags:           { women: ["Bags"], men: ["Bags"], kids: ["Accessories"] },
  Accessories:    { women: ["Accessories"], men: ["Accessories"], kids: ["Accessories"] },
};

/** [department, category, sub-category?], or just [department] when nothing maps. */
export function poshmarkCategory(item: VocabItem, dept: Department): { path: string[]; mapped: boolean } {
  const leaf = depopCategory(item, dept)[1]!;
  const mapped = POSHMARK_CATEGORY[leaf]?.[key(dept)];
  return { path: [dept, ...(mapped ?? [])], mapped: Boolean(mapped) };
}

export function poshmarkColors(colorway: string | null): string[] {
  return [...new Set(depopColors(colorway).map((c) => POSHMARK_COLOR[c]).filter((c): c is string => !!c))].slice(0, 2);
}

/* --- Vinted ---------------------------------------------------------------------- */

export const VINTED_CONDITION: Record<string, string> = {
  nwt: "New with tags",
  nwot: "New without tags",
  excellent: "Very good",
  great: "Very good",
  good: "Good",
  fair: "Satisfactory",
};

const VINTED_COLOR: Record<string, string> = {
  black: "Black", white: "White", gray: "Gray", grey: "Gray",
  charcoal: "Gray", cream: "Cream", ivory: "Cream", offwhite: "Cream",
  beige: "Beige", tan: "Beige", camel: "Beige", sand: "Beige",
  apricot: "Apricot", peach: "Apricot", orange: "Orange", rust: "Orange",
  coral: "Coral", salmon: "Coral", red: "Red", crimson: "Red",
  burgundy: "Burgundy", maroon: "Burgundy", wine: "Burgundy",
  pink: "Pink", blush: "Pink", fuchsia: "Pink", magenta: "Pink",
  hotpink: "Pink", rose: "Rose", purple: "Purple", violet: "Purple",
  plum: "Purple", lilac: "Lilac", lavender: "Lilac",
  lightblue: "Light blue", skyblue: "Light blue", babyblue: "Light blue",
  powderblue: "Light blue", blue: "Blue", cobalt: "Blue", royalblue: "Blue",
  navy: "Navy", turquoise: "Turquoise", teal: "Turquoise", aqua: "Turquoise",
  cyan: "Turquoise", mint: "Mint", seafoam: "Mint", green: "Green",
  emerald: "Green", kellygreen: "Green", sage: "Green",
  darkgreen: "Dark green", forestgreen: "Dark green", huntergreen: "Dark green",
  khaki: "Khaki", olive: "Khaki", armygreen: "Khaki",
  brown: "Brown", chocolate: "Brown", coffee: "Brown", mocha: "Brown",
  mustard: "Mustard", yellow: "Yellow", silver: "Silver", gold: "Gold",
  multi: "Multi", multicolor: "Multi", rainbow: "Multi",
  clear: "Clear", transparent: "Clear",
};

const VINTED_COLOR_BIGRAMS: Array<[RegExp, string]> = [
  [/light[\s-]?blue/g, "lightblue"], [/sky[\s-]?blue/g, "skyblue"],
  [/baby[\s-]?blue/g, "babyblue"], [/powder[\s-]?blue/g, "powderblue"],
  [/royal[\s-]?blue/g, "royalblue"], [/navy[\s-]?blue/g, "navy"],
  [/dark[\s-]?green/g, "darkgreen"], [/forest[\s-]?green/g, "forestgreen"],
  [/hunter[\s-]?green/g, "huntergreen"], [/kelly[\s-]?green/g, "kellygreen"],
  [/army[\s-]?green/g, "armygreen"], [/olive[\s-]?green/g, "olive"],
  [/hot[\s-]?pink/g, "hotpink"], [/off[\s-]?white/g, "offwhite"],
];

const VINTED_MATERIAL_LABELS = [
  "Acrylic", "Alpaca", "Bamboo", "Canvas", "Cardboard", "Cashmere", "Ceramic",
  "Chiffon", "Corduroy", "Cotton", "Denim", "Down", "Elastane", "Faux fur",
  "Faux leather", "Felt", "Flannel", "Fleece", "Foam", "Glass", "Gold",
  "Jute", "Lace", "Latex", "Leather", "Linen", "Merino", "Mesh", "Metal",
  "Mohair", "Neoprene", "Nylon", "Paper", "Patent leather", "Plastic",
  "Polyester", "Porcelain", "Rattan", "Rayon", "Rubber", "Satin", "Sequin",
  "Silicone", "Silk", "Silver", "Steel", "Stone", "Straw", "Suede", "Tulle",
  "Tweed", "Velour", "Velvet", "Wood", "Wool",
];
const VINTED_MATERIAL: Record<string, string> = {
  ...Object.fromEntries(VINTED_MATERIAL_LABELS.map((m) => [m.toLowerCase(), m])),
  poly: "Polyester", spandex: "Elastane", lycra: "Elastane",
  viscose: "Rayon", pleather: "Faux leather", "vegan leather": "Faux leather",
  "faux-leather": "Faux leather", "faux-fur": "Faux fur",
};

const VINTED_ADULT_SIZE: Record<string, string> = {
  XS: "XS", S: "S", M: "M", L: "L", XL: "XL",
  XXL: "XXL", "2XL": "XXL", XXXL: "XXXL", "3XL": "XXXL",
  "4XL": "4XL", "5XL": "5XL", "6XL": "6XL", "7XL": "7XL", "8XL": "8XL",
  OS: "One size", ONESIZE: "One size", OSFA: "One size",
};

/** Kids sizes: exact matches only; a wrong guess misprices the listing. */
const VINTED_KIDS_SIZE: Record<string, string> = {
  PREEMIE: "Preemie", NB: "Newborn", NEWBORN: "Newborn",
  "0-1M": "0-1 m", "1-3M": "1-3 m", "3-6M": "3-6 m", "6-9M": "6-9 m",
  "9-12M": "9-12 m", "12-18M": "12-18 m", "18-24M": "18-24 m",
  "2T": "2T / 2", "2": "2T / 2", "3T": "3T / 3", "3": "3T / 3",
  "4T": "4T / 4", "4": "4T / 4", "5T": "5T / 5", "5": "5T / 5",
  "6": "6 y", "6Y": "6 y", "7": "7 y", "7Y": "7 y", "8": "8 y", "8Y": "8 y",
  "9": "9 y", "9Y": "9 y", "10": "10 y", "10Y": "10 y",
  "11": "11 y", "11Y": "11 y", "12": "12 y", "12Y": "12 y",
};

const VINTED_CATEGORY: Array<[RegExp, { noun: string } & PerDept]> = [
  [/polo/i, { noun: "polo shirt", women: "Polo shirts", men: "Polo shirts", kids: "Polo shirts" }],
  [/wind ?breaker|rain ?coat|puffer|parka|coat/i, { noun: "coat", women: "Coats", men: "Coats", kids: "Coats" }],
  [/jacket|blazer|bomber/i, { noun: "jacket", women: "Jackets", men: "Jackets", kids: "Jackets" }],
  [/hoodie/i, { noun: "hoodie", women: "Hoodies & sweatshirts", men: "Hoodies & sweatshirts", kids: "Hoodies & sweatshirts" }],
  [/sweatshirt|crewneck|pullover/i, { noun: "sweatshirt", women: "Hoodies & sweatshirts", men: "Hoodies & sweatshirts", kids: "Hoodies & sweatshirts" }],
  [/cardigan/i, { noun: "cardigan", women: "Cardigans", men: "Cardigans", kids: "Sweaters" }],
  [/sweater|jumper|knit/i, { noun: "sweater", women: "Sweaters", men: "Sweaters", kids: "Sweaters" }],
  [/t-?shirt|\btee\b|graphic tee/i, { noun: "t-shirt", women: "T-shirts", men: "T-shirts", kids: "T-shirts" }],
  [/tank|cami(sole)?/i, { noun: "tank top", women: "Tank tops", men: "Tank tops", kids: "Tops" }],
  [/blouse/i, { noun: "blouse", women: "Blouses", men: "Shirts", kids: "Tops" }],
  [/button[- ]?(up|down)|flannel|shirt/i, { noun: "shirt", women: "Blouses", men: "Shirts", kids: "Tops" }],
  [/\btop\b/i, { noun: "top", women: "Tops", men: "T-shirts", kids: "Tops" }],
  [/dress/i, { noun: "dress", women: "Dresses", men: "Shirts", kids: "Dresses" }],
  [/romper/i, { noun: "romper", women: "Jumpsuits & rompers", men: "Pants", kids: "Rompers" }],
  [/jumpsuit|overalls|one[- ]?piece/i, { noun: "jumpsuit", women: "Jumpsuits & rompers", men: "Pants", kids: "Outfits & sets" }],
  [/skirt/i, { noun: "skirt", women: "Skirts", men: "Pants", kids: "Skirts" }],
  [/jeans|denim/i, { noun: "jeans", women: "Jeans", men: "Jeans", kids: "Jeans" }],
  [/legging|yoga pant/i, { noun: "leggings", women: "Leggings", men: "Pants", kids: "Leggings" }],
  [/joggers?|sweatpant|track ?pant/i, { noun: "sweatpants", women: "Sweatpants", men: "Sweatpants", kids: "Sweatpants" }],
  [/shorts/i, { noun: "shorts", women: "Shorts", men: "Shorts", kids: "Shorts" }],
  [/pants?|trousers|chinos|slacks/i, { noun: "pants", women: "Pants", men: "Pants", kids: "Pants" }],
  [/swim|bikini|trunks/i, { noun: "swimsuit", women: "Swimwear", men: "Swimwear", kids: "Swimwear" }],
  [/sneaker|trainer|running shoe/i, { noun: "sneakers", women: "Sneakers", men: "Sneakers", kids: "Sneakers" }],
  [/boot/i, { noun: "boots", women: "Boots", men: "Boots", kids: "Boots" }],
  [/heel|pump|sandal|flat|loafer|shoe/i, { noun: "shoes", women: "Shoes", men: "Shoes", kids: "Shoes" }],
  [/purse|handbag|tote|crossbody|bag/i, { noun: "bag", women: "Bags", men: "Bags", kids: "Accessories" }],
  [/hat|cap|beanie|scarf|belt|glove|accessor/i, { noun: "accessory", women: "Accessories", men: "Accessories", kids: "Accessories" }],
];

/** The buyer-search noun and Vinted's lowest category for the department. */
export function vintedCategory(item: VocabItem, dept: Department): { noun: string; leaf: string | null } {
  const hit = firstMatch(item, VINTED_CATEGORY);
  if (hit) return { noun: hit.noun, leaf: hit[key(dept)] };
  return { noun: (item.subcategory ?? item.category ?? "").toLowerCase(), leaf: null };
}

/** Up to two colours from Vinted's palette ("Gray", not Depop's "Grey"). */
export function vintedColors(colorway: string | null): string[] {
  if (!colorway) return [];
  let text = colorway.toLowerCase();
  for (const [re, token] of VINTED_COLOR_BIGRAMS) text = text.replace(re, token);
  const seen = new Set<string>();
  for (const token of text.split(/[^a-z]+/)) {
    const mapped = VINTED_COLOR[token];
    if (mapped) seen.add(mapped);
    if (seen.size === 2) break;
  }
  return [...seen];
}

/** Size label → Vinted's exact option, or null when nothing matches exactly. */
export function vintedSize(sizeLabel: string | null, dept: Department): string | null {
  if (!sizeLabel) return null;
  const k = sizeLabel.toUpperCase().replace(/[\s.]/g, "").replace(/MONTHS?$/, "M");
  return (dept === "Kids" ? VINTED_KIDS_SIZE[k] : VINTED_ADULT_SIZE[k]) ?? null;
}

/** First of the item's materials that maps into Vinted's list. */
export function vintedMaterial(materials: string[]): string | null {
  for (const raw of materials) {
    const cleaned = raw.toLowerCase().replace(/\d+%?/g, "").trim();
    if (VINTED_MATERIAL[cleaned]) return VINTED_MATERIAL[cleaned]!;
    for (const token of cleaned.split(/[^a-z-]+/)) {
      if (VINTED_MATERIAL[token]) return VINTED_MATERIAL[token]!;
    }
  }
  return null;
}

/* --- eBay ------------------------------------------------------------------------ */

/** Condition enums of the Sell Inventory API. */
export const EBAY_CONDITION: Record<string, string> = {
  nwt: "NEW_WITH_TAGS",
  nwot: "NEW_WITHOUT_TAGS",
  excellent: "PRE_OWNED_EXCELLENT",
  great: "PRE_OWNED_EXCELLENT",
  good: "PRE_OWNED_GOOD",
  fair: "PRE_OWNED_FAIR",
};

/** How eBay's listing form names each condition enum. */
export const EBAY_CONDITION_LABEL: Record<string, string> = {
  NEW_WITH_TAGS: "New with tags",
  NEW_WITHOUT_TAGS: "New without tags",
  PRE_OWNED_EXCELLENT: "Pre-owned - Excellent",
  PRE_OWNED_GOOD: "Pre-owned - Good",
  PRE_OWNED_FAIR: "Pre-owned - Fair",
};
